import { useEffect } from 'react';

// Background scroll lock for overlays (sheets, confirm dialogs, lightbox).
//
// Root cause of the Build 81 defect "the bedroom page moves while I scroll the item sheet":
// the page's scroll owner is the DOCUMENT (body scrolls; the sheet backdrop is position:fixed
// on top of it). The sheet body is its own scroll container, but WebKit chains every touch
// scroll the inner container cannot consume — at its top / bottom edge, or whenever the
// sheet content is shorter than the sheet — to the next scrollable ancestor, i.e. the
// document. Wheel events over the backdrop or over a non-scrollable sheet do the same on
// desktop. `overflow: hidden` on body alone is not a fix: WKWebView keeps touch-scrolling an
// overflow-hidden body.
//
// The fix takes the document out of the scrolling business while at least one overlay is
// open: body becomes position:fixed, pinned at the current offset (top = -scrollY) so
// nothing visibly moves, and there is no document scroll range left for a gesture or wheel
// to chain into. The sheet keeps its own scroll container (CSS: .sheet overflow-y:auto +
// overscroll-behavior:contain). On the LAST unlock the inline styles are restored exactly
// and the window is scrolled back to the captured offset — never to the top.
//
// A reference count makes nested overlays (item sheet → finding editor → confirm) share ONE
// capture, so opening and closing any number of sheets never accumulates body styles.

type BodyProp = 'position' | 'top' | 'left' | 'right' | 'width' | 'overflow' | 'paddingRight';
const BODY_PROPS: readonly BodyProp[] = ['position', 'top', 'left', 'right', 'width', 'overflow', 'paddingRight'];

interface LockState {
  count: number;
  scrollX: number;
  scrollY: number;
  saved: Record<BodyProp, string>;
}

let lock: LockState | null = null;

export function bodyScrollLockDepth(): number {
  return lock ? lock.count : 0;
}

export function lockBodyScroll(): void {
  if (typeof document === 'undefined') return;
  if (lock) {
    lock.count += 1;
    return;
  }
  const body = document.body;
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  // Desktop only (iOS has overlay scrollbars): keep the layout width when the document
  // scrollbar disappears with the scroll range.
  const scrollbar = window.innerWidth - document.documentElement.clientWidth;
  const saved = {} as Record<BodyProp, string>;
  for (const prop of BODY_PROPS) saved[prop] = body.style[prop];
  body.style.position = 'fixed';
  body.style.top = `${-scrollY}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';
  if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
  lock = { count: 1, scrollX, scrollY, saved };
}

export function unlockBodyScroll(): void {
  if (!lock) return;
  lock.count -= 1;
  if (lock.count > 0) return;
  const { scrollX, scrollY, saved } = lock;
  lock = null;
  const body = document.body;
  for (const prop of BODY_PROPS) body.style[prop] = saved[prop];
  // Restore only after the document has its scroll range back.
  window.scrollTo(scrollX, scrollY);
}

// Locks for the lifetime of the mounted overlay.
export function useBodyScrollLock(active: boolean = true): void {
  useEffect(() => {
    if (!active) return undefined;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [active]);
}
